import React, { useEffect, useMemo, useState } from "react";
import { Receipt, Building2, Plus, Trash2, Edit3, CheckCircle2, Lock, Unlock, Loader2, Landmark, Calendar as CalendarIcon, Users, Pause, Play, X, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { endpoints } from "@/lib/api";

const money = (amount, currency = "ZAR") => {
  const n = Number(amount || 0);
  const sym = currency === "ZAR" ? "R" : currency === "USD" ? "$" : currency === "EUR" ? "€" : currency === "GBP" ? "£" : "";
  return `${sym}${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const STATUS_TONE = {
  active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  paused: "bg-amber-100 text-amber-700 border-amber-200",
  cancelled: "bg-rose-100 text-rose-700 border-rose-200",
};

export default function SubscriptionsPage() {
  const [me, setMe] = useState(null);
  const [plans, setPlans] = useState([]);
  const [banks, setBanks] = useState([]);
  const [subs, setSubs] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [planModal, setPlanModal] = useState({ open: false, editing: null });
  const [bankModal, setBankModal] = useState({ open: false, editing: null });
  const [subModal, setSubModal] = useState({ open: false, editing: null, prefillSlug: null });
  const [blockModal, setBlockModal] = useState({ open: false, slug: "", name: "", reason: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [p, b, s, c, m] = await Promise.all([
        endpoints.listSubscriptionPlans({ include_inactive: true }),
        endpoints.listBankAccounts({ include_inactive: true }),
        endpoints.listSubscriptions(),
        endpoints.listClients().catch(() => []),
        endpoints.authMe().catch(() => null),
      ]);
      setPlans(p); setBanks(b); setSubs(s); setClients(c); setMe(m);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load subscription data");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const isAdmin = me?.role === "admin";

  if (me && !["admin", "staff", "consultant"].includes(me.role)) {
    return (
      <div className="p-10 max-w-3xl mx-auto">
        <div className="glass-card rounded-3xl p-10 text-center">
          <div className="mx-auto h-12 w-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mb-4"><Lock className="h-5 w-5" /></div>
          <h1 className="font-display text-xl font-extrabold mb-1">Authorized staff only</h1>
          <p className="text-sm text-slate-500">Client subscriptions are managed by ISOQuest staff.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-600 text-white flex items-center justify-center shadow-md">
            <Receipt className="h-5 w-5" />
          </span>
          <div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight">Client Subscriptions</h1>
            <p className="text-xs text-slate-500">Set up monthly or annual plans, pick a payout bank account, and manage who stays active.</p>
          </div>
        </div>
      </div>

      <Tabs defaultValue="control" className="space-y-5">
        <TabsList>
          <TabsTrigger value="control" data-testid="tab-control">Control panel</TabsTrigger>
          <TabsTrigger value="subs" data-testid="tab-subscriptions">All subscriptions</TabsTrigger>
          <TabsTrigger value="plans" data-testid="tab-plans">Plans</TabsTrigger>
          <TabsTrigger value="banks" data-testid="tab-banks">Bank accounts</TabsTrigger>
        </TabsList>

        {/* ------- Control panel (per-client) ------- */}
        <TabsContent value="control" className="space-y-4">
          <ControlPanel
            clients={clients}
            subs={subs}
            plans={plans}
            banks={banks}
            isAdmin={isAdmin}
            onEnrol={(slug) => setSubModal({ open: true, editing: null, prefillSlug: slug })}
            onEdit={(sub) => setSubModal({ open: true, editing: sub })}
            onToggleAccess={(slug, name, reason) => setBlockModal({ open: true, slug, name, reason })}
            onReload={load}
          />
        </TabsContent>

        {/* ------- Client subscriptions ------- */}
        <TabsContent value="subs" className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-slate-500">{subs.length} subscription{subs.length === 1 ? "" : "s"} on file</p>
            {isAdmin && (
              <Button className="brand-gradient text-white" onClick={() => setSubModal({ open: true, editing: null })} data-testid="new-subscription">
                <Plus className="h-4 w-4 mr-1" /> New subscription
              </Button>
            )}
          </div>
          {subs.length === 0 ? (
            <EmptyState icon={Users} title="No client subscriptions yet" subtitle={isAdmin ? "Create a plan, then enrol a client to get started." : "Nothing to show."} />
          ) : (
            <div className="grid gap-3">
              {subs.map((s) => (
                <div key={s.id} data-testid={`subscription-row-${s.id}`} className="glass-card rounded-2xl p-4 flex flex-wrap items-center gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="font-semibold truncate">{s.client_name || s.client_slug}</span>
                      <Badge variant="outline" className={`rounded-full ${STATUS_TONE[s.status] || "bg-slate-100"}`}>{s.status}</Badge>
                      {s.client_access_blocked && <Badge variant="outline" className="rounded-full bg-rose-100 text-rose-700 border-rose-200">Access blocked</Badge>}
                    </div>
                    <div className="text-[13px] text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
                      <span>{s.plan_name || "Custom"} · <b>{money(s.price, s.currency)}</b> / {s.interval === "annual" ? "year" : "month"}</span>
                      {s.bank_account_label && <span className="flex items-center gap-1"><Landmark className="h-3 w-3" />{s.bank_account_label}</span>}
                      {s.next_billing_date && <span className="flex items-center gap-1"><CalendarIcon className="h-3 w-3" />Next due {s.next_billing_date}</span>}
                      {s.last_paid_date && <span>Last paid {s.last_paid_date}</span>}
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={async () => {
                        try { await endpoints.markSubscriptionPaid(s.id); toast.success("Marked paid, next due date rolled forward"); load(); }
                        catch (e) { toast.error(e.response?.data?.detail || "Could not mark paid"); }
                      }} data-testid={`mark-paid-${s.id}`}>
                        <CheckCircle2 className="h-4 w-4 mr-1" /> Mark paid
                      </Button>
                      <Button size="sm" variant="outline" onClick={async () => {
                        try { await endpoints.updateSubscription(s.id, { status: s.status === "paused" ? "active" : "paused" }); load(); }
                        catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
                      }} data-testid={`pause-${s.id}`}>
                        {s.status === "paused" ? <><Play className="h-4 w-4 mr-1" /> Resume</> : <><Pause className="h-4 w-4 mr-1" /> Pause</>}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => {
                        const cli = clients.find((c) => c.slug === s.client_slug) || {};
                        setBlockModal({ open: true, slug: s.client_slug, name: s.client_name || s.client_slug, reason: cli.access_blocked_reason || "" });
                      }} data-testid={`toggle-access-${s.id}`}>
                        {s.client_access_blocked ? <><Unlock className="h-4 w-4 mr-1" /> Restore access</> : <><Lock className="h-4 w-4 mr-1" /> Block access</>}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setSubModal({ open: true, editing: s })} data-testid={`edit-sub-${s.id}`}>
                        <Edit3 className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={async () => {
                        if (!window.confirm("Delete this subscription?")) return;
                        try { await endpoints.deleteSubscription(s.id); toast.success("Subscription deleted"); load(); }
                        catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
                      }} data-testid={`delete-sub-${s.id}`}>
                        <Trash2 className="h-4 w-4 text-rose-500" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ------- Plans ------- */}
        <TabsContent value="plans" className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-slate-500">{plans.length} plan{plans.length === 1 ? "" : "s"}</p>
            {isAdmin && (
              <Button className="brand-gradient text-white" onClick={() => setPlanModal({ open: true, editing: null })} data-testid="new-plan">
                <Plus className="h-4 w-4 mr-1" /> New plan
              </Button>
            )}
          </div>
          {plans.length === 0 ? (
            <EmptyState icon={Receipt} title="No plans yet" subtitle={isAdmin ? "Create reusable pricing like Starter / Pro so you can assign them to clients." : "Nothing to show."} />
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {plans.map((p) => (
                <div key={p.id} data-testid={`plan-row-${p.id}`} className="glass-card rounded-2xl p-4 flex flex-col gap-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="font-semibold truncate">{p.name}</h3>
                      <div className="text-[11px] text-slate-500">{p.interval === "annual" ? "Annual" : "Monthly"} · {p.currency}</div>
                    </div>
                    {!p.active && <Badge variant="outline" className="rounded-full bg-slate-100">Archived</Badge>}
                  </div>
                  <div className="text-2xl font-display font-extrabold">{money(p.price, p.currency)}</div>
                  {p.description && <p className="text-xs text-slate-500">{p.description}</p>}
                  {isAdmin && (
                    <div className="flex items-center gap-2 mt-1">
                      <Button size="sm" variant="outline" onClick={() => setPlanModal({ open: true, editing: p })} data-testid={`edit-plan-${p.id}`}>
                        <Edit3 className="h-4 w-4 mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="outline" onClick={async () => {
                        if (!window.confirm(`Delete plan "${p.name}"?`)) return;
                        try { await endpoints.deleteSubscriptionPlan(p.id); toast.success("Plan deleted"); load(); }
                        catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
                      }} data-testid={`delete-plan-${p.id}`}>
                        <Trash2 className="h-4 w-4 text-rose-500" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ------- Bank accounts ------- */}
        <TabsContent value="banks" className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-slate-500">{banks.length} bank account{banks.length === 1 ? "" : "s"}</p>
            {isAdmin && (
              <Button className="brand-gradient text-white" onClick={() => setBankModal({ open: true, editing: null })} data-testid="new-bank">
                <Plus className="h-4 w-4 mr-1" /> Add account
              </Button>
            )}
          </div>
          {banks.length === 0 ? (
            <EmptyState icon={Landmark} title="No bank accounts yet" subtitle={isAdmin ? "Add the account where clients should pay their subscription fees." : "Nothing to show."} />
          ) : (
            <div className="grid md:grid-cols-2 gap-3">
              {banks.map((b) => (
                <div key={b.id} data-testid={`bank-row-${b.id}`} className="glass-card rounded-2xl p-4 flex flex-col gap-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="font-semibold truncate flex items-center gap-1">
                        {b.label}
                        {b.is_default && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-400" />}
                      </h3>
                      <div className="text-[11px] text-slate-500">{b.bank_name} · {b.currency}</div>
                    </div>
                    {!b.active && <Badge variant="outline" className="rounded-full bg-slate-100">Archived</Badge>}
                  </div>
                  <dl className="text-xs text-slate-600 grid grid-cols-2 gap-y-1">
                    <dt className="text-slate-400">Account holder</dt><dd className="truncate">{b.account_holder}</dd>
                    <dt className="text-slate-400">Account number</dt><dd className="truncate font-mono">{b.account_number}</dd>
                    {b.branch_code && (<><dt className="text-slate-400">Branch code</dt><dd className="truncate font-mono">{b.branch_code}</dd></>)}
                    {b.swift_code && (<><dt className="text-slate-400">SWIFT</dt><dd className="truncate font-mono">{b.swift_code}</dd></>)}
                  </dl>
                  {b.notes && <p className="text-[11px] text-slate-500">{b.notes}</p>}
                  {isAdmin && (
                    <div className="flex items-center gap-2 mt-1">
                      <Button size="sm" variant="outline" onClick={() => setBankModal({ open: true, editing: b })} data-testid={`edit-bank-${b.id}`}>
                        <Edit3 className="h-4 w-4 mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="outline" onClick={async () => {
                        if (!window.confirm(`Delete account "${b.label}"?`)) return;
                        try { await endpoints.deleteBankAccount(b.id); toast.success("Bank account deleted"); load(); }
                        catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
                      }} data-testid={`delete-bank-${b.id}`}>
                        <Trash2 className="h-4 w-4 text-rose-500" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {planModal.open && (
        <PlanModal plan={planModal.editing} onClose={() => setPlanModal({ open: false, editing: null })} onSaved={load} />
      )}
      {bankModal.open && (
        <BankModal bank={bankModal.editing} onClose={() => setBankModal({ open: false, editing: null })} onSaved={load} />
      )}
      {subModal.open && (
        <SubscriptionModal
          existing={subModal.editing}
          prefillSlug={subModal.prefillSlug}
          plans={plans.filter((p) => p.active || (subModal.editing && p.id === subModal.editing.plan_id))}
          banks={banks.filter((b) => b.active || (subModal.editing && b.id === subModal.editing.bank_account_id))}
          clients={clients}
          onClose={() => setSubModal({ open: false, editing: null })}
          onSaved={load}
        />
      )}
      {blockModal.open && (
        <BlockAccessModal
          slug={blockModal.slug}
          clientName={blockModal.name}
          reason={blockModal.reason}
          isCurrentlyBlocked={subs.find((s) => s.client_slug === blockModal.slug)?.client_access_blocked || false}
          onClose={() => setBlockModal({ open: false, slug: "", name: "", reason: "" })}
          onSaved={load}
        />
      )}
    </div>
  );
}

const EmptyState = ({ icon: Icon, title, subtitle }) => (
  <div className="glass-card rounded-3xl p-10 text-center">
    <div className="mx-auto h-12 w-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center mb-4"><Icon className="h-5 w-5" /></div>
    <h2 className="font-display font-bold">{title}</h2>
    <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
  </div>
);

function PlanModal({ plan, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: plan?.name || "", interval: plan?.interval || "monthly",
    price: plan?.price ?? 0, currency: plan?.currency || "ZAR",
    description: plan?.description || "", active: plan?.active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      if (plan) await endpoints.updateSubscriptionPlan(plan.id, { ...form, price: Number(form.price) });
      else await endpoints.createSubscriptionPlan({ ...form, price: Number(form.price) });
      toast.success(plan ? "Plan updated" : "Plan created");
      onSaved?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="plan-modal" className="max-w-md">
        <DialogHeader>
          <DialogTitle>{plan ? "Edit plan" : "New subscription plan"}</DialogTitle>
          <DialogDescription>Define a reusable price you can assign to any client.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Plan name"><Input data-testid="plan-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Starter" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Billing interval">
              <Select value={form.interval} onValueChange={(v) => setForm({ ...form, interval: v })}>
                <SelectTrigger data-testid="plan-interval"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="annual">Annual</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="Currency">
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger data-testid="plan-currency"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="ZAR">ZAR</SelectItem><SelectItem value="USD">USD</SelectItem><SelectItem value="EUR">EUR</SelectItem><SelectItem value="GBP">GBP</SelectItem></SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Price"><Input data-testid="plan-price" type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></Field>
          <Field label="Description (optional)"><Textarea data-testid="plan-desc" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          <div className="flex items-center justify-between">
            <Label className="text-sm">Active (visible when enrolling clients)</Label>
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="plan-active" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="brand-gradient text-white" disabled={busy} onClick={save} data-testid="plan-save">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Save plan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BankModal({ bank, onClose, onSaved }) {
  const [form, setForm] = useState({
    label: bank?.label || "", bank_name: bank?.bank_name || "", account_holder: bank?.account_holder || "",
    account_number: bank?.account_number || "", branch_code: bank?.branch_code || "", swift_code: bank?.swift_code || "",
    currency: bank?.currency || "ZAR", is_default: bank?.is_default ?? false, notes: bank?.notes || "", active: bank?.active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    for (const k of ["label", "bank_name", "account_holder", "account_number"]) {
      if (!form[k]?.trim()) return toast.error(`${k.replace("_", " ")} is required`);
    }
    setBusy(true);
    try {
      if (bank) await endpoints.updateBankAccount(bank.id, form);
      else await endpoints.createBankAccount(form);
      toast.success(bank ? "Account updated" : "Account added");
      onSaved?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="bank-modal" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{bank ? "Edit bank account" : "Add bank account"}</DialogTitle>
          <DialogDescription>Where should clients deposit their subscription fees?</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Label (your reference)"><Input data-testid="bank-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. ZAR operating" /></Field>
            <Field label="Currency">
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger data-testid="bank-currency"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="ZAR">ZAR</SelectItem><SelectItem value="USD">USD</SelectItem><SelectItem value="EUR">EUR</SelectItem><SelectItem value="GBP">GBP</SelectItem></SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Bank name"><Input data-testid="bank-name" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} placeholder="e.g. First National Bank" /></Field>
          <Field label="Account holder"><Input data-testid="bank-holder" value={form.account_holder} onChange={(e) => setForm({ ...form, account_holder: e.target.value })} placeholder="e.g. ISOQuest (Pty) Ltd" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Account number"><Input data-testid="bank-number" value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} /></Field>
            <Field label="Branch code"><Input data-testid="bank-branch" value={form.branch_code} onChange={(e) => setForm({ ...form, branch_code: e.target.value })} /></Field>
          </div>
          <Field label="SWIFT / BIC (optional)"><Input data-testid="bank-swift" value={form.swift_code} onChange={(e) => setForm({ ...form, swift_code: e.target.value })} /></Field>
          <Field label="Notes (optional)"><Textarea data-testid="bank-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. Reference format: INV-xxxx" /></Field>
          <div className="flex items-center justify-between">
            <Label className="text-sm">Mark as default account</Label>
            <Switch checked={form.is_default} onCheckedChange={(v) => setForm({ ...form, is_default: v })} data-testid="bank-default" />
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-sm">Active (available when enrolling clients)</Label>
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="bank-active" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="brand-gradient text-white" disabled={busy} onClick={save} data-testid="bank-save">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Save account
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SubscriptionModal({ existing, prefillSlug, plans, banks, clients, onClose, onSaved }) {
  const defaultBank = banks.find((b) => b.is_default) || banks[0];
  const [form, setForm] = useState({
    client_slug: existing?.client_slug || prefillSlug || "",
    plan_id: existing?.plan_id || (plans[0]?.id || ""),
    price: existing?.price ?? plans[0]?.price ?? 0,
    interval: existing?.interval || plans[0]?.interval || "monthly",
    currency: existing?.currency || plans[0]?.currency || "ZAR",
    bank_account_id: existing?.bank_account_id || defaultBank?.id || "",
    status: existing?.status || "active",
    start_date: existing?.start_date || new Date().toISOString().slice(0, 10),
    next_billing_date: existing?.next_billing_date || "",
    notes: existing?.notes || "",
  });
  const [busy, setBusy] = useState(false);

  const onPlanChange = (planId) => {
    const p = plans.find((x) => x.id === planId);
    setForm((f) => ({ ...f, plan_id: planId, price: p?.price ?? f.price, interval: p?.interval ?? f.interval, currency: p?.currency ?? f.currency }));
  };

  const save = async () => {
    if (!form.client_slug) return toast.error("Pick a client");
    if (!form.plan_id) return toast.error("Pick a plan");
    setBusy(true);
    try {
      const payload = { ...form, price: Number(form.price) };
      if (existing) await endpoints.updateSubscription(existing.id, payload);
      else await endpoints.createSubscription(payload);
      toast.success(existing ? "Subscription updated" : "Client enrolled");
      onSaved?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="sub-modal" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{existing ? "Edit subscription" : "Enrol client in a subscription"}</DialogTitle>
          <DialogDescription>Pick the plan, bank account and starting dates.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Client">
            <Select value={form.client_slug} onValueChange={(v) => setForm({ ...form, client_slug: v })} disabled={!!existing}>
              <SelectTrigger data-testid="sub-client"><SelectValue placeholder="Choose a client" /></SelectTrigger>
              <SelectContent>{clients.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.company_name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          {plans.length === 0 ? (
            <p className="text-xs text-amber-600">Create a plan first before enrolling a client.</p>
          ) : (
            <Field label="Plan">
              <Select value={form.plan_id} onValueChange={onPlanChange}>
                <SelectTrigger data-testid="sub-plan"><SelectValue /></SelectTrigger>
                <SelectContent>{plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {money(p.price, p.currency)}/{p.interval === "annual" ? "yr" : "mo"}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Price (override)"><Input data-testid="sub-price" type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></Field>
            <Field label="Billing interval">
              <Select value={form.interval} onValueChange={(v) => setForm({ ...form, interval: v })}>
                <SelectTrigger data-testid="sub-interval"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="annual">Annual</SelectItem></SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Receive payment into">
            <Select value={form.bank_account_id} onValueChange={(v) => setForm({ ...form, bank_account_id: v })}>
              <SelectTrigger data-testid="sub-bank"><SelectValue placeholder="Pick a bank account" /></SelectTrigger>
              <SelectContent>{banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.label} · {b.bank_name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start date"><Input data-testid="sub-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></Field>
            <Field label="Next due"><Input data-testid="sub-next" type="date" value={form.next_billing_date} onChange={(e) => setForm({ ...form, next_billing_date: e.target.value })} /></Field>
          </div>
          <Field label="Status">
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="sub-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="paused">Paused</SelectItem><SelectItem value="cancelled">Cancelled</SelectItem></SelectContent>
            </Select>
          </Field>
          <Field label="Notes (optional)"><Textarea data-testid="sub-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="brand-gradient text-white" disabled={busy} onClick={save} data-testid="sub-save">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} {existing ? "Save changes" : "Enrol client"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BlockAccessModal({ slug, clientName, reason, isCurrentlyBlocked, onClose, onSaved }) {
  const [why, setWhy] = useState(reason || (isCurrentlyBlocked ? "" : "Subscription unpaid — please settle to restore access."));
  const [busy, setBusy] = useState(false);
  const apply = async (block) => {
    setBusy(true);
    try {
      await endpoints.toggleClientAccess(slug, { access_blocked: block, reason: block ? why : "" });
      toast.success(block ? "Client access blocked" : "Client access restored");
      onSaved?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="block-modal" className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isCurrentlyBlocked ? "Restore access" : "Block client access"}</DialogTitle>
          <DialogDescription>
            {isCurrentlyBlocked
              ? `Unblock client logins for ${clientName}. They will be able to sign in again.`
              : `Prevent all users at ${clientName} from signing in. ISOQuest staff still have full access.`}
          </DialogDescription>
        </DialogHeader>
        {!isCurrentlyBlocked && (
          <Field label="Reason shown to the client">
            <Textarea data-testid="block-reason" rows={3} value={why} onChange={(e) => setWhy(e.target.value)} />
          </Field>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {isCurrentlyBlocked ? (
            <Button className="brand-gradient text-white" disabled={busy} onClick={() => apply(false)} data-testid="block-restore">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Unlock className="h-4 w-4 mr-1" />} Restore access
            </Button>
          ) : (
            <Button className="bg-rose-500 hover:bg-rose-600 text-white" disabled={busy} onClick={() => apply(true)} data-testid="block-confirm">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Lock className="h-4 w-4 mr-1" />} Block access
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, children }) => (
  <div><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div></div>
);

function ControlPanel({ clients, subs, plans, banks, isAdmin, onEnrol, onEdit, onToggleAccess, onReload }) {
  const [filter, setFilter] = useState("all"); // all | active | blocked | unenrolled
  const subBySlug = useMemo(() => Object.fromEntries(subs.map((s) => [s.client_slug, s])), [subs]);

  const rows = clients.map((c) => ({
    client: c,
    sub: subBySlug[c.slug] || null,
  }));

  const filtered = rows.filter((r) => {
    if (filter === "active") return r.sub && r.sub.status === "active" && !r.client.access_blocked;
    if (filter === "blocked") return r.client.access_blocked;
    if (filter === "unenrolled") return !r.sub;
    return true;
  });

  const totals = useMemo(() => {
    const mrr = subs.filter((s) => s.status === "active").reduce((acc, s) => acc + Number(s.interval === "annual" ? s.price / 12 : s.price), 0);
    const blocked = clients.filter((c) => c.access_blocked).length;
    const enrolled = subs.length;
    return { mrr, blocked, enrolled, total: clients.length };
  }, [subs, clients]);

  if (clients.length === 0) {
    return (
      <EmptyState icon={Users} title="No clients yet" subtitle="Add a client from the Clients page first, then enrol them in a subscription here." />
    );
  }

  return (
    <>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCard label="Total clients" value={totals.total} />
        <SummaryCard label="Enrolled" value={totals.enrolled} tone="indigo" />
        <SummaryCard label="Access blocked" value={totals.blocked} tone="rose" />
        <SummaryCard label="Est. monthly recurring" value={money(totals.mrr, subs[0]?.currency || "ZAR")} tone="emerald" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {[
          { k: "all", label: "All clients" },
          { k: "active", label: "Active" },
          { k: "blocked", label: "Access blocked" },
          { k: "unenrolled", label: "Not enrolled" },
        ].map((f) => (
          <button key={f.k} onClick={() => setFilter(f.k)} data-testid={`filter-${f.k}`}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${filter === f.k ? "bg-indigo-600 text-white border-indigo-600" : "bg-white/60 text-slate-600 border-slate-200 hover:bg-white"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Users} title="Nothing to show" subtitle="Clear the filter or enrol a client to see them here." />
      ) : (
        <div className="grid gap-3">
          {filtered.map(({ client, sub }) => (
            <ControlPanelRow key={client.slug} client={client} sub={sub} plans={plans} banks={banks}
              isAdmin={isAdmin}
              onEnrol={() => onEnrol(client.slug)}
              onEdit={() => sub && onEdit(sub)}
              onToggleAccess={() => onToggleAccess(client.slug, client.company_name, client.access_blocked_reason || "")}
              onReload={onReload}
            />
          ))}
        </div>
      )}
    </>
  );
}

const SummaryCard = ({ label, value, tone = "slate" }) => {
  const tones = {
    slate: "from-slate-500 to-slate-700",
    indigo: "from-indigo-500 to-fuchsia-600",
    rose: "from-rose-500 to-pink-600",
    emerald: "from-emerald-500 to-teal-600",
  };
  return (
    <div className="glass-card rounded-2xl p-4">
      <div className={`text-[11px] uppercase tracking-widest bg-gradient-to-r ${tones[tone]} bg-clip-text text-transparent font-semibold`}>{label}</div>
      <div className="font-display font-extrabold text-2xl mt-1">{value}</div>
    </div>
  );
};

function ControlPanelRow({ client, sub, plans, banks, isAdmin, onEnrol, onEdit, onToggleAccess, onReload }) {
  const [savingPrice, setSavingPrice] = useState(false);
  const [price, setPrice] = useState(sub?.price ?? "");
  const [planId, setPlanId] = useState(sub?.plan_id || "");
  const [bankId, setBankId] = useState(sub?.bank_account_id || "");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setPrice(sub?.price ?? "");
    setPlanId(sub?.plan_id || "");
    setBankId(sub?.bank_account_id || "");
    setDirty(false);
  }, [sub?.id, sub?.price, sub?.plan_id, sub?.bank_account_id]);

  const quickSave = async () => {
    if (!sub) return;
    setSavingPrice(true);
    try {
      await endpoints.updateSubscription(sub.id, {
        plan_id: planId || null,
        price: Number(price) || 0,
        bank_account_id: bankId || null,
      });
      toast.success("Pricing updated");
      setDirty(false);
      onReload?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSavingPrice(false); }
  };

  const statusBadge = client.access_blocked
    ? <Badge variant="outline" className="rounded-full bg-rose-100 text-rose-700 border-rose-200">Access blocked</Badge>
    : sub?.status === "paused" ? <Badge variant="outline" className="rounded-full bg-amber-100 text-amber-700 border-amber-200">Paused</Badge>
    : sub?.status === "cancelled" ? <Badge variant="outline" className="rounded-full bg-slate-200 text-slate-700 border-slate-300">Cancelled</Badge>
    : sub ? <Badge variant="outline" className="rounded-full bg-emerald-100 text-emerald-700 border-emerald-200">Active</Badge>
    : <Badge variant="outline" className="rounded-full bg-slate-100 text-slate-700 border-slate-200">Not enrolled</Badge>;

  return (
    <div data-testid={`control-row-${client.slug}`} className="glass-card rounded-2xl p-4">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        {client.logo_url ? (
          <img src={client.logo_url} alt="" className="h-9 w-9 rounded-lg object-contain bg-white border border-slate-200" />
        ) : (
          <div className="h-9 w-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center font-semibold">{(client.company_name || "?").slice(0, 1)}</div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold truncate">{client.company_name}</span>
            {statusBadge}
          </div>
          <div className="text-[12px] text-slate-500 flex flex-wrap gap-x-3 gap-y-1">
            {sub?.next_billing_date && <span className="inline-flex items-center gap-1"><CalendarIcon className="h-3 w-3" />Next due {sub.next_billing_date}</span>}
            {sub?.last_paid_date && <span>Last paid {sub.last_paid_date}</span>}
            {client.access_blocked_reason && <span className="text-rose-600 truncate" title={client.access_blocked_reason}>Reason: {client.access_blocked_reason}</span>}
          </div>
        </div>
      </div>

      {sub && isAdmin ? (
        <div className="grid md:grid-cols-[1fr,1fr,1fr,auto,auto,auto,auto] gap-2 items-end">
          <Field label="Plan">
            <Select value={planId} onValueChange={(v) => { setPlanId(v); const p = plans.find((x) => x.id === v); if (p) setPrice(p.price); setDirty(true); }}>
              <SelectTrigger data-testid={`row-plan-${client.slug}`}><SelectValue placeholder="— pick plan —" /></SelectTrigger>
              <SelectContent>{plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {money(p.price, p.currency)}/{p.interval === "annual" ? "yr" : "mo"}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={`Price (${sub.currency}/${sub.interval === "annual" ? "yr" : "mo"})`}>
            <Input data-testid={`row-price-${client.slug}`} type="number" step="0.01" value={price}
              onChange={(e) => { setPrice(e.target.value); setDirty(true); }} />
          </Field>
          <Field label="Receive payment into">
            <Select value={bankId} onValueChange={(v) => { setBankId(v); setDirty(true); }}>
              <SelectTrigger data-testid={`row-bank-${client.slug}`}><SelectValue placeholder="— pick account —" /></SelectTrigger>
              <SelectContent>{banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Button size="sm" disabled={!dirty || savingPrice} onClick={quickSave} className="brand-gradient text-white" data-testid={`row-save-${client.slug}`}>
            {savingPrice ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
          <Button size="sm" variant="outline" onClick={async () => {
            try { await endpoints.markSubscriptionPaid(sub.id); toast.success("Marked paid"); onReload?.(); }
            catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
          }} data-testid={`row-mark-paid-${client.slug}`}>
            <CheckCircle2 className="h-4 w-4 mr-1" /> Mark paid
          </Button>
          <Button size="sm" variant="outline" onClick={onToggleAccess} data-testid={`row-access-${client.slug}`}>
            {client.access_blocked ? <><Unlock className="h-4 w-4 mr-1" /> Restore</> : <><Lock className="h-4 w-4 mr-1" /> Block</>}
          </Button>
          <Button size="sm" variant="outline" onClick={onEdit} data-testid={`row-edit-${client.slug}`}>
            <Edit3 className="h-4 w-4" />
          </Button>
        </div>
      ) : sub ? (
        <div className="text-[13px] text-slate-500">
          {sub.plan_name || "Custom"} · <b>{money(sub.price, sub.currency)}</b> / {sub.interval === "annual" ? "year" : "month"}
          {sub.bank_account_label ? ` · paid to ${sub.bank_account_label}` : ""}
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <p className="text-[13px] text-slate-500 flex-1">This client isn't on a subscription yet.</p>
          {isAdmin && (
            <Button size="sm" className="brand-gradient text-white" onClick={onEnrol} data-testid={`enrol-${client.slug}`}>
              <Plus className="h-4 w-4 mr-1" /> Enrol
            </Button>
          )}
          {isAdmin && (
            <Button size="sm" variant="outline" onClick={onToggleAccess} data-testid={`row-access-${client.slug}`}>
              {client.access_blocked ? <><Unlock className="h-4 w-4 mr-1" /> Restore</> : <><Lock className="h-4 w-4 mr-1" /> Block</>}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
